// src/app/shared/transaction/all-transaction/transaction-history-table/Filters.tsx
"use client";

import { Button, Flex, Input } from "rizzui";
import { type Table as ReactTableType } from "@tanstack/react-table";
import { PiMagnifyingGlassBold, PiTrashDuotone } from "react-icons/pi";

interface TableToolbarProps<T extends Record<string, any>> {
  table: ReactTableType<T>;
}

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "success", label: "Success" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const currencyOptions = [
  { value: "", label: "All Currency" },
  { value: "NGN", label: "NGN" },
  { value: "USDT", label: "USDT" },
];

export default function Filters<TData extends Record<string, any>>({
  table,
}: TableToolbarProps<TData>) {
  // detect if any filter (column or global) is active
  const isFiltered =
    Boolean(table.getState().globalFilter) ||
    table.getState().columnFilters.length > 0;

  return (
    <Flex
      align="center"
      direction="col"
      className="mt-6 @3xl:flex-row @3xl:items-center @3xl:gap-4"
    >
      {/* Status & Currency dropdowns */}
      <Flex align="center" className="gap-2 order-1">
        <select
          className="h-9 rounded border px-2 w-[110px]"
          value={table.getColumn("status")?.getFilterValue() ?? ""}
          onChange={(e) =>
            table
              .getColumn("status")
              ?.setFilterValue(e.target.value || undefined)
          }
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          className="h-9 rounded border px-2 w-[130px]"
          value={table.getColumn("currencyType")?.getFilterValue() ?? ""}
          onChange={(e) =>
            table
              .getColumn("currencyType")
              ?.setFilterValue(e.target.value || undefined)
          }
        >
          {currencyOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Flex>

      {/* Clear button */}
      {isFiltered && (
        <Button
          variant="flat"
          onClick={() => {
            table.resetColumnFilters();
            table.resetGlobalFilter();
          }}
          className="order-2 h-9 w-full bg-gray-200/70 @3xl:w-auto"
        >
          <PiTrashDuotone className="me-1.5 size-4" /> Clear
        </Button>
      )}

      {/* Global search (e.g. by referenceId or donor) */}
      <Input
        type="search"
        clearable
        inputClassName="h-[36px]"
        placeholder="Search payments..."
        value={table.getState().globalFilter ?? ""}
        onClear={() => table.setGlobalFilter(undefined)}
        onChange={(e) => table.setGlobalFilter(e.target.value)}
        prefix={<PiMagnifyingGlassBold className="size-4" />}
        className="order-3 w-full @3xl:ms-auto @3xl:max-w-sm"
      />
    </Flex>
  );
}
